import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider } from "@/contexts/AuthContext";
import { dismissSplash } from "@/lib/splash";
import { TabBarProvider } from "@/components/nav/TabBarContext";
import MobileTabBar from "@/components/nav/MobileTabBar";
import FeedbackHost from "@/components/feedback/FeedbackHost";
import { Toaster } from "sonner";
import AuthCallback from "@/components/auth/AuthCallback";
import ProtectedRoute from "@/components/auth/ProtectedRoute";

import Landing from "@/pages/Landing";
import DanyThread from "@/pages/DanyThread";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import SignupRedirect from "@/pages/SignupRedirect";
import PhoneVerify from "@/pages/PhoneVerify";
import ClientDashboard from "@/pages/ClientDashboard";
import ProviderDashboard from "@/pages/ProviderDashboard";
import PostJob from "@/pages/PostJob";
import NewJobRoute from "@/pages/NewJobRoute";
import ClientJobs from "@/pages/ClientJobs";
import JobDetail from "@/pages/JobDetail";
import BrowseJobs from "@/pages/BrowseJobs";
import DirectedRequests from "@/pages/DirectedRequests";
import SendQuote from "@/pages/SendQuote";
import ProviderQuotes from "@/pages/ProviderQuotes";
import Chat from "@/pages/Chat";
import ProvidersDirectory from "@/pages/ProvidersDirectory";
import PublicJobs from "@/pages/PublicJobs";
import PublicJobDetail from "@/pages/PublicJobDetail";
import ProviderLanding from "@/pages/ProviderLanding";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminUsers from "@/pages/AdminUsers";
import AdminUserDetail from "@/pages/AdminUserDetail";
import AdminVerifications from "@/pages/AdminVerifications";
import MasterLandingEditor from "@/pages/MasterLandingEditor";
import Settings from "@/pages/Settings";
import Panel from "@/pages/Panel";
import PaymentMethods from "@/pages/PaymentMethods";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Unsubscribe from "@/pages/Unsubscribe";
import Promo from "@/pages/Promo";
import AdminJobs from "@/pages/AdminJobs";
import AdminConfig from "@/pages/AdminConfig";
import AdminBroadcasts from "@/pages/AdminBroadcasts";
import AdminTickets from "@/pages/AdminTickets";
import AdminFeedback from "@/pages/AdminFeedback";
import AdminAcquisition from "@/pages/AdminAcquisition";
import AdminCategories from "@/pages/AdminCategories";
import BillingPage from "@/pages/BillingPage";
import VerificationPage from "@/pages/VerificationPage";
import PaymentLanding from "@/pages/PaymentLanding";
import Pricing from "@/pages/Pricing";
import HowItWorks from "@/pages/HowItWorks";
import BecomeFixyBee from "@/pages/BecomeFixyBee";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import RefundPolicy from "@/pages/RefundPolicy";
import Guides from "@/pages/Guides";
import PostJobGuide from "@/pages/PostJobGuide";
import FAQ from "@/pages/FAQ";
import NotFound from "@/pages/NotFound";
import PushOptIn from "@/components/notifications/PushOptIn";
import VerifyEmailBanner from "@/components/auth/VerifyEmailBanner";
import ScrollToTop from "@/components/layout/ScrollToTop";
import { DanyUiProvider } from "@/components/dany/DanyUiContext";
import DanyFab from "@/components/dany/DanyFab";

import "@/App.css";

function AppRouter() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Landing />} />

      {/* Dany's conversational surface. One surface, two doors: the home hero's
          composer and the global FAB both land here. Public — Dany answers
          anonymous visitors (DANY.md §2.3). */}
      <Route path="/dany" element={<DanyThread />} />
      <Route path="/dany/:threadId" element={<DanyThread />} />

      <Route path="/login" element={<Login />} />
      {/* Two doors, and the door is the role (2026-10-28): no screen asks
          "client or FixyBee?" any more. Bare `/signup` and `/onboarding` stay
          as redirects for links already out in the world. */}
      <Route path="/signup/client" element={<Signup key="client" role="client" />} />
      <Route path="/signup/provider" element={<Signup key="provider" role="provider" />} />
      <Route path="/signup" element={<SignupRedirect />} />
      <Route path="/onboarding" element={<SignupRedirect />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/providers" element={<ProvidersDirectory />} />
      <Route path="/open-jobs" element={<PublicJobs />} />
      <Route path="/open-jobs/:job_id" element={<PublicJobDetail />} />
      <Route path="/pro/:slug" element={<ProviderLanding />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/become-a-fixybee" element={<BecomeFixyBee />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/guides" element={<Guides />} />
      <Route path="/guides/post-a-job" element={<PostJobGuide />} />
      <Route path="/guides/:role" element={<Guides />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/pay/:txnId" element={<PaymentLanding />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      {/* Public by law, not by oversight: CAN-SPAM forbids requiring a
          login to opt out. See pages/Unsubscribe.jsx. */}
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/promo/:code" element={<Promo />} />

      {/* Role-agnostic job form. Public CTAs point their returnTo here because
          an anonymous visitor has no namespace yet; see NewJobRoute. */}
      <Route path="/jobs/new" element={<ProtectedRoute><NewJobRoute /></ProtectedRoute>} />

      {/* The mobile Panel index (T12-M4a). Role-neutral like `/jobs/new`: it
          renders the sections of whichever role is signed in, and redirects to
          that role's dashboard above 992px, where the sidebar already holds
          all of it. */}
      <Route path="/panel" element={<ProtectedRoute><Panel /></ProtectedRoute>} />
      {/* Panel > Cobros (T12-M4b). A section of the Panel, so it lives under
          it; reachable above 992px too, which is where Settings now links. */}
      <Route
        path="/panel/payment-methods"
        element={<ProtectedRoute requireRole="provider"><PaymentMethods /></ProtectedRoute>}
      />

      {/* Client */}
      <Route path="/client/dashboard" element={<ProtectedRoute requireRole="client"><ClientDashboard /></ProtectedRoute>} />
      <Route path="/client/jobs" element={<ProtectedRoute requireRole="client"><ClientJobs /></ProtectedRoute>} />
      <Route path="/client/jobs/new" element={<ProtectedRoute requireRole="client"><PostJob /></ProtectedRoute>} />
      <Route path="/client/jobs/:jobId" element={<ProtectedRoute requireRole="client"><JobDetail /></ProtectedRoute>} />
      {/* T19 F3. The post-a-job form, precharged with the job and saving it
          with PATCH — one form for both, so an edit cannot produce a post the
          form could not have created. */}
      <Route path="/client/jobs/:jobId/edit" element={<ProtectedRoute requireRole="client"><PostJob /></ProtectedRoute>} />
      <Route path="/client/messages" element={<ProtectedRoute requireRole="client"><Chat /></ProtectedRoute>} />
      <Route path="/client/settings" element={<ProtectedRoute requireRole="client"><Settings /></ProtectedRoute>} />

      {/* Provider */}
      <Route path="/provider/dashboard" element={<ProtectedRoute requireRole="provider"><ProviderDashboard /></ProtectedRoute>} />
      <Route path="/provider/browse" element={<ProtectedRoute requireRole="provider"><BrowseJobs /></ProtectedRoute>} />
      <Route path="/provider/jobs/:jobId" element={<ProtectedRoute requireRole="provider"><JobDetail /></ProtectedRoute>} />
      <Route path="/provider/jobs/:jobId/edit" element={<ProtectedRoute requireRole="provider"><PostJob /></ProtectedRoute>} />
      {/* R6. Requests a client sent to this FixyBee by name. A sibling of
          `/provider/requests` (the jobs they posted themselves) and not a child
          of it: the two lists are about opposite ends of the same word, and a
          path under `/provider/requests/` would have read as the same place. */}
      <Route path="/provider/requests-for-you" element={<ProtectedRoute requireRole="provider"><DirectedRequests /></ProtectedRoute>} />
      {/* DY2.6. A FixyBee posts jobs as the requester, and every route in this
          app is namespaced by role — so their own jobs need an address inside
          `/provider/*` or `ProtectedRoute` bounces them out of the page they
          just created. Same two components as the client side; only the links
          between them are namespaced (`lib/ownerRoutes`). The DETAIL view is
          the existing `/provider/jobs/:jobId`, which now tells owner from
          quoter by ownership rather than by role. */}
      <Route path="/provider/requests" element={<ProtectedRoute requireRole="provider"><ClientJobs /></ProtectedRoute>} />
      <Route path="/provider/requests/new" element={<ProtectedRoute requireRole="provider"><PostJob /></ProtectedRoute>} />
      <Route path="/provider/quotes" element={<ProtectedRoute requireRole="provider"><ProviderQuotes /></ProtectedRoute>} />
      <Route path="/provider/quotes/new" element={<ProtectedRoute requireRole="provider"><SendQuote /></ProtectedRoute>} />
      <Route path="/provider/messages" element={<ProtectedRoute requireRole="provider"><Chat /></ProtectedRoute>} />
      <Route path="/provider/billing" element={<ProtectedRoute requireRole="provider"><BillingPage /></ProtectedRoute>} />
      <Route path="/provider/verification" element={<ProtectedRoute requireRole="provider"><VerificationPage /></ProtectedRoute>} />
      {/* The phone code, moved out of sign-up (2026-10-28). Its own address so
          the quote form's refusal and the dashboard checklist can link to it. */}
      <Route path="/provider/verify-phone" element={<ProtectedRoute requireRole="provider"><PhoneVerify /></ProtectedRoute>} />
      <Route path="/provider/landing-editor" element={<ProtectedRoute requireRole="provider"><MasterLandingEditor /></ProtectedRoute>} />
      <Route path="/provider/settings" element={<ProtectedRoute requireRole="provider"><Settings /></ProtectedRoute>} />

      {/* Generic /chat — redirects to role chat */}
      <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />

      {/* Admin */}
      <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute requireAdmin><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/users/:userId" element={<ProtectedRoute requireAdmin><AdminUserDetail /></ProtectedRoute>} />
      <Route path="/admin/verifications" element={<ProtectedRoute requireAdmin><AdminVerifications /></ProtectedRoute>} />
      <Route path="/admin/jobs" element={<ProtectedRoute requireAdmin><AdminJobs /></ProtectedRoute>} />
      <Route path="/admin/categories" element={<ProtectedRoute requireAdmin><AdminCategories /></ProtectedRoute>} />
      <Route path="/admin/config" element={<ProtectedRoute requireAdmin><AdminConfig /></ProtectedRoute>} />
      <Route path="/admin/broadcasts" element={<ProtectedRoute requireAdmin><AdminBroadcasts /></ProtectedRoute>} />
      <Route path="/admin/tickets" element={<ProtectedRoute requireAdmin><AdminTickets /></ProtectedRoute>} />
      <Route path="/admin/feedback" element={<ProtectedRoute requireAdmin><AdminFeedback /></ProtectedRoute>} />
      <Route path="/admin/acquisition" element={<ProtectedRoute requireAdmin><AdminAcquisition /></ProtectedRoute>} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

function App() {
  // The splash is in index.html so it paints before this bundle exists; the
  // first effect of the root component is the earliest honest signal that the
  // app is actually on screen behind it.
  useEffect(() => {
    dismissSplash();
  }, []);

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <BrowserRouter>
        <AuthProvider>
          {/* DanyUiProvider carries the one fact the FAB and the home hero share:
              whether the hero is currently on screen. The FAB is mounted once,
              outside the router, so it survives navigation and decides for
              itself which pages it belongs on. */}
          <DanyUiProvider>
            {/* T12-M1. Below 992px the bar IS the navigation, and it is mounted
                here — once, outside the router — for the same reason the FAB is:
                a component remounted by every route change cannot animate out,
                and the menu sheet would close on every navigation. */}
            <TabBarProvider>
              <ScrollToTop />
              <AppRouter />
              <DanyFab />
              <MobileTabBar />
              <PushOptIn />
              <VerifyEmailBanner />
              {/* T15. Mounted here for the reason the bar and the FAB are: the
                  three moments that arm the survey all navigate away or close a
                  modal, so a host owned by any of those pages would unmount
                  before it could be seen. */}
              <FeedbackHost />
              {/* T15-M3, and it is a fix that predates T15: `sonner`'s Toaster
                  was mounted NOWHERE, so every `toast.success` in this app has
                  been a silent no-op — the three in VerifyEmailBanner included.
                  Found by the emulation harness while checking that the survey
                  says thank you. See AUDIT.md §11 finding 41.

                  `top-center` and not sonner's default bottom: below 992px the
                  bottom of the screen belongs to the tab bar and its FAB, and a
                  toast there would land on top of them.

                  Styled with this app's tokens rather than through
                  components/ui/sonner.jsx, which is shadcn scaffolding wired to
                  `next-themes` and to `bg-background`/`text-foreground` — a
                  provider this app does not mount and variables it does not
                  define. */}
              <Toaster
                position="top-center"
                offset={12}
                toastOptions={{
                  style: {
                    background: "var(--paper)",
                    color: "var(--worker)",
                    border: "1px solid var(--sand)",
                    fontFamily: "var(--font-display)",
                    fontWeight: 600,
                  },
                }}
              />
            </TabBarProvider>
          </DanyUiProvider>
        </AuthProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
